import type { FAQ, FAQDocumentWithTask } from "@frontierx/shared/schemas/FAQ";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PartySocket from "partysocket";
import z from "zod";
import { useOrganization } from "../Auth/UnifiedAuthProvider";

import { FAQList } from "./FAQList";

const FAQsResponseSchema = z.array(
  z.object({
    id: z.string(),
    question: z.string(),
    idealAnswer: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
);

interface FAQManagerUIProps {
  partySocket: PartySocket;
}

export function FAQManagerUI({ partySocket }: Readonly<FAQManagerUIProps>) {
  const { organization } = useOrganization();
  const queryClient = useQueryClient();

  const fetchFAQs = async (orgId: string): Promise<FAQ[]> => {
    const response = await PartySocket.fetch(
      {
        host: partySocket.host,
        party: "main",
        room: orgId,
        path: "api/faqs/list",
      },
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orgId }),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return FAQsResponseSchema.parse(result);
  };

  const {
    data: faqs,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["faqs", organization?.id],
    queryFn: () => fetchFAQs(organization?.id ?? ""),
    enabled: !!organization?.id,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!organization?.id) throw new Error("No organization ID");

      const response = await PartySocket.fetch(
        {
          host: partySocket.host,
          party: "main",
          room: organization.id,
          path: "api/faqs/delete",
        },
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, orgId: organization.id }),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faqs", organization?.id] });
    },
  });

  const addMutation = useMutation({
    mutationFn: async (faq: { question: string; idealAnswer: string }) => {
      if (!organization?.id) throw new Error("No organization ID");

      const response = await PartySocket.fetch(
        {
          host: partySocket.host,
          party: "main",
          room: organization.id,
          path: "api/faqs/create",
        },
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...faq,
            orgId: organization.id,
            tags: [], // Add required fields from FAQSchema
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error ?? "Failed to create FAQ");
      }

      const result = await response.json();
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faqs", organization?.id] });
    },
  });

  const editMutation = useMutation({
    mutationFn: async ({
      id,
      faq,
    }: {
      id: string;
      faq: { question: string; idealAnswer: string };
    }) => {
      if (!organization?.id) throw new Error("No organization ID");

      const response = await PartySocket.fetch(
        {
          host: partySocket.host,
          party: "main",
          room: organization.id,
          path: "api/faqs/update",
        },
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id,
            ...faq,
            orgId: organization.id,
          }),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error ?? "Failed to update FAQ");
      }

      const result = await response.json();
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faqs", organization?.id] });
    },
  });

  const handleAdd = async (faq: { question: string; idealAnswer: string }) => {
    try {
      await addMutation.mutateAsync(faq);
      return true;
    } catch (error) {
      console.error("Error creating FAQ:", error);
      return false;
    }
  };

  const handleEdit = async (
    id: string,
    faq: { question: string; idealAnswer: string },
  ) => {
    try {
      await editMutation.mutateAsync({ id, faq });
      return true;
    } catch (error) {
      console.error("Error updating FAQ:", error);
      return false;
    }
  };

  const faqsWithTask: FAQDocumentWithTask[] = (faqs ?? []).map((faq) => ({
    ...faq,
    task: undefined, // FAQs don't have processing tasks like documents do
  }));

  return (
    <FAQList
      faqs={faqsWithTask}
      isLoading={isLoading}
      isError={isError}
      onDelete={(id) => deleteMutation.mutate(id)}
      onAdd={handleAdd}
      onEdit={handleEdit}
      isDeleting={deleteMutation.isPending}
    />
  );
}
